import { getResource } from "@dashboard/library";
import Cookies from "js-cookie";
import { COOKIE_DOMAINS, COOKIE_KEYS } from "@/constants/auth.constants";
import { AIRCALL_AUTH_API_ENDPOINTS } from "@/constants/endpoints.constants";
import { getEnv, isDev } from "@/helpers/environment/environment.helpers";
import { SAME_SITE_TYPE } from "@/providers/AuthenticationProvider/AuthenticationProvider.decl";

export function getCookieDomain() {
  return COOKIE_DOMAINS[getEnv()];
}

export function getAuthToken() {
  return Cookies.get(COOKIE_KEYS.AC_TOKEN) || null;
}

export function getAuthRefreshToken() {
  return Cookies.get(COOKIE_KEYS.AC_REFRESH_TOKEN) || null;
}

export function setAuthCookies(token: string, refreshToken: string) {
  setCookie(COOKIE_KEYS.AC_TOKEN, { value: token });
  setCookie(COOKIE_KEYS.AC_REFRESH_TOKEN, { value: refreshToken });
}

export function removeAuthCookies() {
  for (const cookie of [
    COOKIE_KEYS.AC_TOKEN,
    COOKIE_KEYS.AC_REFRESH_TOKEN,
    COOKIE_KEYS.IS_CONNECTED_AS,
  ]) {
    removeCookie(cookie);
  }
}

/**
 * Sets a cookie with given parameters.
 * @param name - key of the cookie
 * @param value - value of the cookie
 * @param domain - domain of the cookie
 * @param path - path of the cookie
 */
export type SetCookieOptions<T extends string> = {
  value: T;
  domain?: string;
  sameSite?: SAME_SITE_TYPE;
  path?: string;
};

export function setCookie<T extends string>(
  name: string,
  {
    value,
    domain = getCookieDomain(),
    sameSite = SAME_SITE_TYPE.NONE,
    path = "/",
  }: SetCookieOptions<T>
): void {
  Cookies.set(name, value, {
    domain,
    path,
    ...(!isDev() && { sameSite, secure: true }),
  });
}

/**
 * Removes a cookie based on its name.
 * @param name - name of the cookie
 */
export type RemoveCookieOptions = {
  sameSite?: SAME_SITE_TYPE;
  domain?: string;
};

export function removeCookie(
  name: string,
  {
    sameSite = SAME_SITE_TYPE.NONE,
    domain = getCookieDomain(),
  }: RemoveCookieOptions = {}
): void {
  // We make sure we remove old cookies as well
  Cookies.remove(name);
  Cookies.remove(name, { domain });
  Cookies.remove(name, { domain, path: "/" });

  if (!isDev()) {
    Cookies.remove(name, {
      domain,
      path: "/",
      sameSite,
      secure: true,
    });
  }
}

interface CognitoTokenResponse {
  accessToken: string;
  expiresIn: number;
  idToken: string;
  refreshToken: string;
  refreshTokenExpirationDate: number;
}

export async function requestRefreshToken({
  token,
  refreshToken,
}: {
  token: string;
  refreshToken: string;
}): Promise<CognitoTokenResponse> {
  const url = AIRCALL_AUTH_API_ENDPOINTS.REFRESH_TOKEN;
  return getResource(url, {
    authorization: `Bearer ${refreshToken}`,
    "x-id-token": token,
    "Aircall-Platform": "dashboard-v4",
  });
}
